import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { DOCUMENT } from "@angular/common";
import { AbstractControl, NgControl } from "@angular/forms";
import { CaptchaService } from "../../service/captcha.service";
import { filter, Subscription } from "rxjs";

/**
 * This captcha component injects a script to render the captcha element from the loaded scripts.
 * It asynchronously loads the script if it is not present, and then initializes the captcha element with callback
 * functions being bound to the window object and this component. It supports Reactive forms with form control
 * being applied to the component.
 *
 * Some of the code was inspired from the implementation of ngx-captcha library:
 * https://github.com/Enngage/ngx-captcha/blob/master/projects/ngx-captcha-lib/src/lib/components/base-re-captcha-component.directive.ts
 *
 * And this pull request:
 * https://github.com/ngx-formly/ngx-formly/issues/2934
 */
@Component({
  selector: 'app-captcha',
  templateUrl: './captcha.component.html',
  styleUrls: ['./captcha.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[id]': 'id',
  }
})
export class CaptchaComponent extends FieldType<FieldTypeConfig> implements AfterViewInit, OnInit, OnDestroy {

  widgetId: number | undefined = undefined;
  control?: AbstractControl | null;
  @ViewChild('wrapper', { static: false}) wrapper!: ElementRef;

  private captcaResetSubscription!: Subscription;

  constructor(
      private captchaService: CaptchaService,
      private zone: NgZone,
      private renderer2: Renderer2,
      private injector: Injector,
      @Inject(DOCUMENT) private document: Document,
      @Inject('CAPTCHA_KEY') private captchaKey: string
  ) { super(); }

  ngOnInit(): void {
    // Below code works with the global window object, and sets up a callback function which is called
    // by the Google Captcha after the script is loaded into the page
    //@ts-ignore
    window.initCaptcha = () => this.initializeGoogleCaptchaComplete();
    //@ts-ignore
    window.captchaSubmitCallback = (response) => this.captchaSubmitCallback(response);
    //@ts-ignore
    window.captchaErrorCallback = (response) => this.captchaErrorCallback(response);
    //@ts-ignore
    window.captchaExpiredCallback = (response) => this.captchaExpiredCallback(response);

    // subscribe to the captcha service to listen for any reset captcha events
    this.captcaResetSubscription = this.captchaService.getResetCaptcha$()
        .pipe(filter(value => value !== undefined && value === this.widgetId))
        .subscribe(value => this.resetCaptcha());
  }

  ngOnDestroy() {
    if(this.captcaResetSubscription) {
      this.captcaResetSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    // If the Google Captcha scripts is not on the dom, we need to manually create it and append it.
    // Otherwise, we want to initialize the captcha into the form.
    if(document.getElementById("recaptcha") === null) {
      let script = this.renderer2.createElement("script");
      script.id = "googleCaptcha"
      script.type = "text/javascript";
      script.src = `https://www.google.com/recaptcha/api.js?onload=initCaptcha&render=explicit`;
      script.defer = true;
      script.async = true;
      this.renderer2.appendChild(this.document.body, script);
    } else {
      this.initializeGoogleCaptchaComplete();
    }
  }

  private initializeGoogleCaptchaComplete(): void {
    this.zone.run(() => {
      // Set the callback functions for the async initialization of captcha
      //@ts-ignore
      this.widgetId = grecaptcha.render(
          this.wrapper.nativeElement,
          {
            "sitekey": this.captchaKey,
            //@ts-ignore
            "callback" : "captchaSubmitCallback",
            //@ts-ignore
            "error-callback": "captchaErrorCallback",
            //@ts-ignore
            "expired-callback": "captchaExpiredCallback"
          }
      );
      this.control = this.injector.get<NgControl | undefined>(
          NgControl,
          undefined,
          { optional: true }
      )?.control;
    });
  }

  private captchaSubmitCallback(response: string): void {
    this.zone.run(() => this.formControl.setValue({ reCaptchaResponseToken: response, reCaptchaWidgetId: this.widgetId }));
  }

  private captchaErrorCallback(response: any): void {
    this.zone.run(() => this.formControl.setValue(undefined));
  }

  private captchaExpiredCallback(response: any): void {
    this.zone.run(() => this.formControl.setValue(undefined));
  }

  private resetCaptcha() {
    this.zone.run(() => {
      //@ts-ignore
      grecaptcha.reset(this.widgetId);
    });
  }
}
